import { differenceInSeconds, isThisYear, isToday, isYesterday, isTomorrow, parseISO, format, add } from "date-fns"
import {
  APP_24_HOUR_TIME_FORMAT,
  APP_CALENDAR_DATE_FORMAT,
  APP_CALENDAR_DATE_WITH_YEAR_FORMAT,
  APP_DATE_AS_DATA_FORMAT,
} from "src/global/config.ts"
import { pluralize } from "src/global/utils/strings.ts"

export function formatLocalDateAsData(iso: string) {
  /*
  ~'this day and this time' in local timezone
  ex. "05/31/2023, 16:00:00 PTD"
   */
  return Intl.DateTimeFormat([], APP_DATE_AS_DATA_FORMAT).format(parseISO(iso))
}

export type ElapsedTime =
  | "LessThanMin"
  | "LessThanHour"
  | "Today"
  | "Yesterday"
  | "ThisYear"
  | "MoreThanYear"
  | "Tomorrow"
  | undefined

export const formatOutputBasedOnAbsoluteDate = (outputFormat: string, elapsedTime: ElapsedTime) => {
  // Combine this with a prefix sentence. e.g. "Last Validated " + "on April 29, 2014 at 20:00"

  // Use "on" when starting with Date format
  if (elapsedTime === "ThisYear" || elapsedTime === "MoreThanYear") return `on ${outputFormat}`
  // Set to lowerCase for these other cases and omit using "on"
  return outputFormat.toLowerCase()
}
const defaultOutputFormatter = (prefix: string) => prefix

/**
 * Formats a date string to a human-readable format based on the elapsed time since the date.
 * @param dateString - The date string to format.
 * @param outputFormatter - Optional function that formats the output based on the elapsed time since the date provided.
 * @param timeFormat - Optional time format to use. Defaults to 24-hour time format, it excludes time from output when set to "null".
 * @returns A human-readable date string. See requirements for more details https://greatexpectations.atlassian.net/wiki/spaces/SUP/pages/1070333974/Date+time+display+in+UI
 */
export function formatLocalCalendarDateWithTime(
  dateString: string,
  outputFormatter: (prefix: string, elapsedTime?: ElapsedTime) => string = defaultOutputFormatter,
  timeFormat: Intl.DateTimeFormatOptions | null = APP_24_HOUR_TIME_FORMAT,
) {
  const targetDate = parseISO(dateString)
  if (isNaN(targetDate.getTime())) {
    return dateString // Return value if string is not a valid date format
  }
  const elapsedSeconds = differenceInSeconds(new Date(), targetDate)
  const elapsedMinutes = Math.floor(elapsedSeconds / 60)
  const absMinutes = Math.abs(elapsedMinutes)
  const timeString = timeFormat && Intl.DateTimeFormat([], timeFormat).format(targetDate)
  const timeStringOutput = timeString ? ` at ${timeString}` : ""

  // Less than a minute ago
  if (elapsedSeconds > 0 && elapsedSeconds < 60) {
    return outputFormatter("Less than a minute ago", "LessThanMin")
  }

  // Now
  if (elapsedSeconds === 0) {
    return "Now"
  }

  // In less than a minute
  if (elapsedSeconds > -60 && elapsedSeconds < 0) {
    return outputFormatter("In less than a minute", "LessThanMin")
  }

  // X minutes ago
  if (elapsedMinutes > 0 && elapsedMinutes < 60) {
    return outputFormatter(`${elapsedMinutes} ${pluralize("minute", elapsedMinutes)} ago`, "LessThanHour")
  }

  // In X minutes
  if (elapsedMinutes > -60 && elapsedMinutes < 0) {
    return outputFormatter(`In ${absMinutes} ${pluralize("minute", absMinutes)}`, "LessThanHour")
  }

  // Today at 20:00
  if (isToday(targetDate)) {
    return outputFormatter(`Today${timeStringOutput}`, "Today")
  }

  // Yesterday at 20:00
  if (isYesterday(targetDate)) {
    return outputFormatter(`Yesterday${timeStringOutput}`, "Yesterday")
  }

  // Tomorrow at 20:00
  if (isTomorrow(targetDate)) {
    return outputFormatter(`Tomorrow${timeStringOutput}`, "Tomorrow")
  }

  // April 29 at 20:00
  if (isThisYear(targetDate))
    return outputFormatter(
      `${Intl.DateTimeFormat([], APP_CALENDAR_DATE_FORMAT).format(targetDate)}${timeStringOutput}`,
      "ThisYear",
    )

  // April 29, 2014 at 20:00
  return outputFormatter(
    `${Intl.DateTimeFormat([], APP_CALENDAR_DATE_WITH_YEAR_FORMAT).format(targetDate)}${timeStringOutput}`,
    "MoreThanYear",
  )
}

export function formatDateForTable(iso: string) {
  /*
  ~'this day and time' in timezone
  ex. "2020-01-01" or "2020-01-01 01:02:03"
   */
  // A TIMESTAMP_NTZ may belong to UTC, but it is not guaranteed. It could belong to a predetermined timezone not encoded into data

  function includesTimezone(iso: string) {
    return iso.includes("Z") || iso.includes("+")
  }

  function containsISODate(iso: string) {
    const regex = /\d{4}-[0-9]{2}-[0-9]{2}.*/ // "[2024-01-01]..."
    return regex.test(iso)
  }

  function cleanISO(iso: string) {
    return iso.replace("T00:00:00.000Z", "").replace(".000", "").replace("T", " ").replace("Z", "")
  }

  if (containsISODate(iso) && !includesTimezone(iso)) {
    // Snowflake's TIMESTAMP_NTZ means that there is no corresponding timezone encoded into data.
    // However, ISO8601 states that a timestamp with no timezone should be interpreted using local timezone.
    // Javascript automatically uses server timezone, which is undesirable in this scenario
    // As a result, do not parse ISO strings missing timezone
    return cleanISO(iso)
  }
  // Format ISO strings such as "Nov 19 2022"
  const reformatted = new Date(iso).toISOString()
  return cleanISO(reformatted)
}

// getFirstDayNextMonthLongFmt returns the first day of the next month
// e.g. now is 2023-09-15 => October 1, 2023
// e.g. now is 2023-12-01 => January 1, 2024
export const getFirstDayNextMonthLongFmt = (): string => {
  return format(add(new Date(), { months: 1 }), "MMMM 1, yyyy")
}
